define('presentation/welcome/viewModels/onboardingInformationViewModel',[
    'i18next',
    'businessServices/country/countryCode',
    'businessServices/country/countryRegions',
    'common/promises/promiseFactory',
    'constants/countryEnumerations',
    'presentation/welcome/common/onboardingNavigationManager',
    'presentation/welcome/common/onboardingSectionValidationDisplay',
    'presentation/welcome/facades/onboardingInformationFacade',
    'presentation/welcome/validators/onboardingInformationValidator',
    'presentation/welcome/viewModels/teamMemberViewModel'
], function() {
    return function() {
        let self = this;

        const CountryCodeConstructor = require('businessServices/country/countryCode');
        const _countries = new CountryCodeConstructor();

        const CountryRegionsConstructor = require('businessServices/country/countryRegions');
        const _countryRegions = new CountryRegionsConstructor();

        const PromiseFactoryConstructor = require('common/promises/promiseFactory');
        const _promiseFactory = new PromiseFactoryConstructor();

        const TeamMemberViewModelConstructor = require('presentation/welcome/viewModels/teamMemberViewModel');

        const SectionValidationDisplayConstructor = require('presentation/welcome/common/onboardingSectionValidationDisplay');
        const _sectionValidationDisplay = new SectionValidationDisplayConstructor();

        const _countryEnumerations = require('constants/countryEnumerations');
        const _i18n = require('i18next');

        let _facade = null;
        let _disposables = [];
        let _autoSaveDisposables = [];
        let _onboardingNavigationManager = null;
        let _validator = null;
        let _billingAddress = null;

        const MAX_ITEMS_CAN_ADD = 10;

        self.countries = null;
        self.statesProvinces = null;

        self.isScrollToOffsetActive = true;
        self.isCompositionComplete = ko.observable(false);
        self.showValidation = ko.observable(false);
        self.isScrollActive = ko.observable(false);

        self.headingText = _i18n.t('onboardingInformation:headingText');
        self.subtitleText = _i18n.t('onboardingInformation:subtitleText');
        self.isValid = ko.observable(false);

        self.addressLineOne = ko.observable('').extend({ rateLimit: 500 });
        self.addressLineTwo = ko.observable('').extend({ rateLimit: 500 });
        self.city = ko.observable('').extend({ rateLimit: 500 });
        self.stateProvince = ko.observable("");
        self.postalCode = ko.observable("");
        self.country = ko.observable("");
        self.phoneNumber = ko.observable('').extend({ rateLimit: 500 });
        self.phoneNumberCountryAbbreviation = ko.observable(_countryEnumerations.usa);
        self.faxNumber = ko.observable('').extend({ rateLimit: 500 });
        self.faxNumberCountryAbbreviation = ko.observable(_countryEnumerations.usa);
        self.emailAddress = ko.observable('').extend({ rateLimit: 500 });
        self.websiteUrl = ko.observable('').extend({ rateLimit: 500 });
        self.teamMembers = ko.observableArray([]);
        self.useBillingAddress = ko.observable(false);

        self.shouldDisplayBusinessLocation = ko.observable(false);
        self.shouldDisplayPhoneNumber = ko.observable(false);
        self.shouldDisplayFaxNumber = ko.observable(false);
        self.shouldDisplayEmailAddress = ko.observable(false);
        self.shouldDisplayWebsiteUrl = ko.observable(false);
        self.shouldDisplayTeamMembers = ko.observable(false);

        _sectionValidationDisplay.init(self.showValidation);
        self.canDisplayBusinessLocationValidation = _sectionValidationDisplay.createSectionDisplay(self.shouldDisplayBusinessLocation);
        self.canDisplayPhoneNumberValidation = _sectionValidationDisplay.createSectionDisplay(self.shouldDisplayPhoneNumber);
        self.canDisplayFaxNumberValidation = _sectionValidationDisplay.createSectionDisplay(self.shouldDisplayFaxNumber);
        self.canDisplayEmailAddressValidation = _sectionValidationDisplay.createSectionDisplay(self.shouldDisplayEmailAddress);
        self.canDisplayWebsiteUrlValidation = _sectionValidationDisplay.createSectionDisplay(self.shouldDisplayWebsiteUrl);
        self.canDisplayTeamMembersValidation = _sectionValidationDisplay.createSectionDisplay(self.shouldDisplayTeamMembers);

        self.stateOrProvinceLabelI18nKey = ko.pureComputed(() => {
            return _countries.findByAbbreviation(self.country()[0]).hasProvinces ?
                'onboardingInformation:province' :
                'onboardingInformation:state';
        });
        self.zipOrPostalCodeMetaData = ko.pureComputed(() => _countries.findByAbbreviation(self.country()[0]).hasProvinces ?
            {i18nKey: 'onboardingInformation:postalCode', type: 'postalcode', maxLength: 6} :
            {i18nKey: 'onboardingInformation:zipCode', type: 'zipcode', maxLength: 5}
        );

        self.canAddAnotherTeamMember = ko.pureComputed(() => self.teamMembers().length < MAX_ITEMS_CAN_ADD);
        self.addTeamMemberBtnText = ko.pureComputed(() => {
            if (self.canAddAnotherTeamMember()) {
                return _i18n.t('onboardingInformation:addTeamMember');
            } else {
                return _i18n.t('onboardingInformation:disabledAddTeamMember');
            }
        });

        self.isAddressEditable = ko.pureComputed(() => self.useBillingAddress() === false);

        self.isNavigateToNextPageActive = ko.pureComputed(() => {
            if (self.isValid() !== true) {
                return false;
            }
            if (self.shouldDisplayTeamMembers() === false) {
                return true;
            }
            const hasAtLeastOneTeamMember = self.teamMembers()
                .some((teamMember) => teamMember.name().trim() !== '');
            const areTeamMembersValid = self.teamMembers()
                .every((teamMember) => teamMember.name.isValid() !== false);
            return hasAtLeastOneTeamMember && areTeamMembersValid;
        });
        self.selectPlaceHolderMessage = ko.pureComputed(() => _i18n.t("onboardingIdentity:select"));

        self.onRemoveTeamMemberClick = (memberToDelete) => {
            if (self.teamMembers().length <= 1) {
                memberToDelete.name.forceUpdate(true);
                memberToDelete.name('');
                memberToDelete.title.forceUpdate(true);
                memberToDelete.title('');
                return;
            }
            self.teamMembers.remove(memberToDelete);
        };

        self.onAddTeamMemberClick = () => {
            if (self.canAddAnotherTeamMember() === false) {
                return;
            }
            _addDefaultTeamMember();
        };

        self.onForwardArrowClicked = () => {
            return _promiseFactory.defer((deferredObject) => {
                _validator.validate()
                    .fail(deferredObject.reject)
                    .done((isValid) => {
                        self.showValidation(true);
                        _sectionValidationDisplay.captureDisplayedSections();

                        if (!isValid) {
                            deferredObject.resolve(false);
                        } else {
                            _saveInformation()
                                .fail(deferredObject.reject)
                                .done(() => deferredObject.resolve(true));
                        }
                    });
            });
        };

        const _prepareStatesProvinces = (statesProvinces) => {
            return statesProvinces.map((state) => {
                state.id = state.abbreviation;
                state.text = state.name;
                return state;
            });
        };

        const _currentCountry = (country = null) => {
            if (country){
                const currentCountry = _countries.findByAbbreviation(country);
                return self.countries.find(item => item.text === currentCountry.text);
            } else {
                const currentCountry = _countries.getDefaultCountry();
                return self.countries.find(item => item.text === currentCountry.text);
            }
        };

        const _onCountryChange = (countryCode) => {
            self.postalCode("");
            self.statesProvinces(_prepareStatesProvinces(_countryRegions.findByAbbreviation(countryCode[0]).statesProvinces));
            self.stateProvince(self.statesProvinces()[0].id);
        };

        const _populateStateDropdown = () => {
            self.statesProvinces =  ko.observableArray(_prepareStatesProvinces(_countryRegions.findByAbbreviation(_countryEnumerations.usa).statesProvinces));
        };

        const _populateCountryDropdown = () => {
            self.countries =  _countries.countryCodes.map((country) => {
                country.id = country.countryAbbreviation;
                country.text = country.name;
                return country;
            });
        };

        const _handleAddressLineOneChanged = () => {
            _facade.saveAddressLineOne(self.addressLineOne());
        };
        const _handleAddressLineTwoChanged = () => {
            _facade.saveAddressLineTwo(self.addressLineTwo());
        };

        const _handleCityChanged = () => {
            _facade.saveCity(self.city());
        };

        const _handleStateProvinceChanged = () => {
            if (self.stateProvince() && self.country()) {
                _facade.saveStateOrProvince(self.country()[0], self.stateProvince()[0]);
            }
        };

        const _handlePostalCodeChanged = () => {
            if (self.country()) {
                _facade.savePostalCode(self.country()[0], self.postalCode());
            }
        };

        const _handlePhoneNumberChanged = () => {
            if (self.phoneNumber.isValid()) {
                _facade.savePhoneNumber(self.phoneNumber());
            }
        };

        const _handleFaxNumberChanged = () => {
            if (self.faxNumber.isValid()) {
                _facade.saveFaxNumber(self.faxNumber());
            }
        };

        const _handleEmailAddressChanged = () => {
            if (self.emailAddress.isValid()) {
                _facade.saveEmailAddress(self.emailAddress());
            }
        };

        const _handleWebsiteUrlChanged = () => {
            if (self.websiteUrl.isValid()) {
                _facade.saveWebsiteUrl(self.websiteUrl());
            }
        };

        const _mapTeamMembers = () => {
            return self.teamMembers()
                .filter((member) => {
                    return !(member.name().trim() === "" && member.title().trim() === "");
                })
                .map((member) => {
                    if (member.name() && member.title()) {
                        return `${member.name()} - ${member.title()}`;
                    } else {
                        return member.name();
                    }
                });
        };

        const _handleTeamMembersChanged = () => {
            _facade.saveTeamMembers(_mapTeamMembers());
        };

        const _handleUseBillingAddressChanged = (useBillingAddress) => {
            if (useBillingAddress !== true || !_billingAddress) {
                return;
            }

            if (_billingAddress.countryCode) {
                self.country([_currentCountry(_billingAddress.countryCode).id]);
            }

            if (_billingAddress.regionCode) {
                const billingState = self.statesProvinces().find((state) => state.id === _billingAddress.regionCode);
                if (billingState) {
                    self.stateProvince([billingState.id]);
                }
            }

            self.postalCode.forceUpdate(true);
            self.postalCode(_billingAddress.postalCode || '');

            self.addressLineOne.forceUpdate(true);
            self.addressLineOne(_billingAddress.addressLineOne || '');

            self.addressLineTwo.forceUpdate(true);
            self.addressLineTwo(_billingAddress.addressLineTwo || '');

            self.city.forceUpdate(true);
            self.city(_billingAddress.city || '');
        };

        const _handleDisplayBusinessLocationChanged = () => {
            self.addressLineOne.validate();
            self.city.validate();
            self.postalCode.validate();
        };

        const _handleDisplayPhoneNumberChanged = () => {
            self.phoneNumber.validate();
        };

        const _handleDisplayFaxNumberChanged = () => {
            self.faxNumber.validate();
        };

        const _handleDisplayEmailAddressChanged = () => {
            self.emailAddress.validate();
        };

        const _handleDisplayWebsiteUrlChanged = () => {
            self.websiteUrl.validate();
        };

        const _handleDisplayTeamMembersChanged = () => {
            if (self.shouldDisplayTeamMembers() && self.teamMembers().length === 0) {
                _addDefaultTeamMember();
            }
            _revalidateTeamMembers();
        };

        const _revalidateTeamMembers = () => {
            self.teamMembers().forEach((teamMember) => teamMember.name.validate());
        };

        const _selectedValue = (observable) => {
            const value = observable();
            return Array.isArray(value) ? value[0] : value;
        };

        const _orNull = (value) => {
            if (value === undefined || value === null || value.toString().trim() === '') {
                return null;
            }
            return value;
        };

        const _saveInformation = () => {
            // These are rate-limited by 500ms, so one could still be pending. Stop them first.
            _autoSaveDisposables.forEach(subscription => subscription.dispose());
            _autoSaveDisposables = [];

            const isLocationShared = self.shouldDisplayBusinessLocation();

            return _facade.saveOnboardingInformation({
                addressLineOne: isLocationShared ? _orNull(self.addressLineOne()) : null,
                addressLineTwo: isLocationShared ? _orNull(self.addressLineTwo()) : null,
                city: isLocationShared ? _orNull(self.city()) : null,
                regionCode: isLocationShared ? _orNull(_selectedValue(self.stateProvince)) : null,
                postalCode: isLocationShared ? _orNull(self.postalCode()) : null,
                countryCode: isLocationShared ? _orNull(_selectedValue(self.country)) : null,
                phoneNumber: self.shouldDisplayPhoneNumber() ? _orNull(self.phoneNumber()) : null,
                faxNumber: self.shouldDisplayFaxNumber() ? _orNull(self.faxNumber()) : null,
                emailAddress: self.shouldDisplayEmailAddress() ? _orNull(self.emailAddress()) : null,
                websiteUrl: self.shouldDisplayWebsiteUrl() ? _orNull(self.websiteUrl()) : null,
                teamMembers: self.shouldDisplayTeamMembers() ? _mapTeamMembers() : []
            });
        };

        const _addDefaultTeamMember = () => {
            const member = new TeamMemberViewModelConstructor(self);
            self.teamMembers.push(member);
            _subscribeToMemberObservables(member);
            _reIndexTeamMembers();
        };

        const _reIndexTeamMembers = () => {
            self.teamMembers().forEach((member, index) => member.index = index);
        };

        const _extractTeamMemberInfo = (memberString) => {
            if (memberString.includes(' - ')) {
                const [name, title] = memberString.split(' - ');
                return { name, title };
            }

            return { name: memberString };
        };

        const _populateInformationForm = (onboardingSession) => {
            const { addressLineOne, addressLineTwo, city, region, postalCode, country, phoneNumber, faxNumber, emailAddress, websiteUrl, teamMembers  } = onboardingSession;
            if (addressLineOne) {
                self.addressLineOne(addressLineOne);
            }
            if (addressLineTwo) {
                self.addressLineTwo(addressLineTwo);
            }
            if (city) {
                self.city(city);
            }
            if (postalCode) {
                self.postalCode(postalCode);
            }
            if (country) {
                self.country([_currentCountry(country).id]);
                self.statesProvinces(_prepareStatesProvinces(_countryRegions.findByAbbreviation(self.country()[0]).statesProvinces));
            } else {
                self.country([_currentCountry().id]);
                self.statesProvinces(_prepareStatesProvinces(_countryRegions.findByAbbreviation(self.country()[0]).statesProvinces));
            }
            if (region) {
                const currentState = self.statesProvinces().find((state) => {
                    return state.id === region;
                });
                if (currentState) {
                    self.stateProvince([currentState.id]);
                } else {
                    self.stateProvince(self.statesProvinces()[0].id);
                }
            } else {
                self.stateProvince(self.statesProvinces()[0].id);
            }

            if (addressLineOne || addressLineTwo || city || postalCode || region) {
                self.shouldDisplayBusinessLocation(true);
            }

            if (phoneNumber) {
                self.phoneNumber(phoneNumber);
                self.shouldDisplayPhoneNumber(true);
            }

            if (faxNumber) {
                self.faxNumber(faxNumber);
                self.shouldDisplayFaxNumber(true);
            }

            if (emailAddress) {
                self.emailAddress(emailAddress);
                self.shouldDisplayEmailAddress(true);
            }

            if (websiteUrl) {
                self.websiteUrl(websiteUrl);
                self.shouldDisplayWebsiteUrl(true);
            }

            if (teamMembers.length === 0) {
                _addDefaultTeamMember();
            } else {
                teamMembers.map((teamMember, index) => {
                    const memberInfo = _extractTeamMemberInfo(teamMember);
                    const member = new TeamMemberViewModelConstructor(self);
                    member.name(memberInfo.name);
                    member.title(memberInfo.title);
                    member.index = index;
                    self.teamMembers.push(member);
                    _subscribeToMemberObservables(member);
                });
                self.shouldDisplayTeamMembers(true);
            }

        };

        const _seedValidation = () => {
            self.addressLineOne.validate();
            self.city.validate();
            self.postalCode.validate();
            self.phoneNumber.validate();
            self.faxNumber.validate();
            self.emailAddress.validate();
            self.websiteUrl.validate();
            _revalidateTeamMembers();
        };

        const _subscribeToMemberObservables = (member) => {
            _autoSaveDisposables.push(member.name.subscribe(_handleTeamMembersChanged));
            _autoSaveDisposables.push(member.title.subscribe(_handleTeamMembersChanged));
        };

        self.detached = () => {
            _onboardingNavigationManager.detached();
            _sectionValidationDisplay.detached();
            _disposables.forEach(subscription => subscription.dispose());
            _disposables = [];
            _autoSaveDisposables.forEach(subscription => subscription.dispose());
            _autoSaveDisposables = [];
        };

        self.compositionComplete = () => {
            self.isCompositionComplete(true);
        };

        self.activate = () => {
            const FacadeConstructor = require('presentation/welcome/facades/onboardingInformationFacade');
            _facade = new FacadeConstructor();
            _facade.init(_promiseFactory);

            const ValidatorConstructor = require('presentation/welcome/validators/onboardingInformationValidator');
            _validator = new ValidatorConstructor();

            const OnboardingNavigationManagerConstructor = require('presentation/welcome/common/onboardingNavigationManager');
            _onboardingNavigationManager = new OnboardingNavigationManagerConstructor();
            _onboardingNavigationManager.init();

            return _initialize();
        };

        const _initialize = () => {
            _promiseFactory.deferIndefinitely(deferredObject => {
                _populateCountryDropdown();
                _populateStateDropdown();

                const initPromiseFactory = new PromiseFactoryConstructor();

                initPromiseFactory.deferIndefinitely(onboardingSessionPromise => {
                    _facade.getLiveReceptionistOnboarding()
                        .fail(onboardingSessionPromise.reject)
                        .done((onboardingSession) => {
                            _populateInformationForm(onboardingSession);
                            _autoSaveDisposables.push(
                                self.addressLineOne.subscribe(_handleAddressLineOneChanged),
                                self.addressLineTwo.subscribe(_handleAddressLineTwoChanged),
                                self.city.subscribe(_handleCityChanged),
                                self.stateProvince.subscribe(_handleStateProvinceChanged),
                                self.postalCode.subscribe(_handlePostalCodeChanged),
                                self.phoneNumber.subscribe(_handlePhoneNumberChanged),
                                self.faxNumber.subscribe(_handleFaxNumberChanged),
                                self.emailAddress.subscribe(_handleEmailAddressChanged),
                                self.websiteUrl.subscribe(_handleWebsiteUrlChanged),
                                self.teamMembers.subscribe(_handleTeamMembersChanged)
                            );
                            _disposables.push(
                                self.country.subscribe(_onCountryChange),
                                self.shouldDisplayBusinessLocation.subscribe(_handleDisplayBusinessLocationChanged),
                                self.shouldDisplayPhoneNumber.subscribe(_handleDisplayPhoneNumberChanged),
                                self.shouldDisplayFaxNumber.subscribe(_handleDisplayFaxNumberChanged),
                                self.shouldDisplayEmailAddress.subscribe(_handleDisplayEmailAddressChanged),
                                self.shouldDisplayWebsiteUrl.subscribe(_handleDisplayWebsiteUrlChanged),
                                self.shouldDisplayTeamMembers.subscribe(_handleDisplayTeamMembersChanged),
                                self.useBillingAddress.subscribe(_handleUseBillingAddressChanged)
                            );
                            _validator.registerViewModel(self, _facade);
                            _seedValidation();
                            onboardingSessionPromise.resolve();
                        });
                });

                initPromiseFactory.deferIndefinitely(billingAddressPromise => {
                    _facade.getBillingAddress()
                        .fail(billingAddressPromise.reject)
                        .done((billingAddress) => {
                            _billingAddress = billingAddress;
                            billingAddressPromise.resolve();
                        });
                });

                initPromiseFactory.wait()
                    .fail(deferredObject.reject)
                    .done(() => deferredObject.resolve());
            });

            return _promiseFactory.wait();
        };
    };
});

